// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import {
  addDays,
  addYears,
  format,
  parseISO,
  subDays,
  subMonths,
  subYears,
} from "date-fns";

import {
  CARAS_COMPONENT_NAMES,
  ParoleCarasFactor,
  ParoleCase,
  ParoleCaseNote,
  paroleCaseSchema,
  ParoleConductRecord,
  paroleConductRecordSchema,
  ParoleHearing,
  paroleHearingSchema,
  ParoleRiskAssessment,
  ParoleRiskNeedFactor,
  ParoleRiskNeedScale,
  ParoleSubcategoryScore,
} from "./schema";

// US_CO has no Parole backend yet (TODO(OBT-41775): replace this fixture with real
// data once one exists). This is the same fixture data used in the Parole
// POC (recidiviz-dashboards#14777) so it's easier to cross-test against that
// reference. It's generated relative to whenever the module is loaded (rather
// than anchored to a fixed historical date) so that "upcoming hearings"
// always look current in a demo, regardless of when `nx offline staff` is run.
const iso = (date: Date): string => format(date, "yyyy-MM-dd");

// The two Parole-enabled tenants, each with its own conduct classification
// scheme (see US_CO.ts/US_ID.ts paroleConfig.conductClassificationColors) --
// used to vary conduct history severities below so both states' schemes are
// demoable via `nx offline staff`.
export type ParoleFixtureStateCode = "US_CO" | "US_ID";

// CARAS v7's logistic-regression coefficients, one per item in
// CARAS_COMPONENT_NAMES order. They are fixed properties of the tool, so
// buildCarasFactors pairs them with each case's own raw item values.
const CARAS_COEFFICIENTS = [
  0.32, 0.16, -0.03, 0.25, 0.18, 0.11, 0.08, 0.26, 0.18, 0.09, 0.09, 0.16,
] as const satisfies { length: (typeof CARAS_COMPONENT_NAMES)["length"] };

function buildCarasFactors(values: Array<number>): Array<ParoleCarasFactor> {
  return CARAS_COMPONENT_NAMES.map((name, i) => ({
    name,
    coefficient: CARAS_COEFFICIENTS[i],
    value: values[i],
  }));
}

// CARAS v7's overall score is a logistic-regression probability, not a sum of
// points out of a max like the other tools -- summing each factor's own
// contribution (value * coefficient) with the model's fixed intercept gives
// the log-odds, which converts to a 0-100 "risk score" via the standard
// logistic function. Deriving `score` from `carasFactors` here (rather than
// hand-typing an independent number) keeps the subcategory chart and the
// overall score mathematically consistent.
const CARAS_INTERCEPT = -2.1;

function computeCarasScore(factors: Array<ParoleCarasFactor>): number {
  const logOdds =
    CARAS_INTERCEPT +
    factors.reduce((sum, f) => sum + f.value * f.coefficient, 0);
  const probability = 1 / (1 + Math.exp(-logOdds));
  return Math.round(probability * 100);
}

function buildCarasAssessment(
  values: Array<number>,
  date: string,
): {
  tool: "CARAS";
  score: number;
  maxScore: number;
  date: string;
  carasFactors: Array<ParoleCarasFactor>;
} {
  const carasFactors = buildCarasFactors(values);
  return {
    tool: "CARAS",
    score: computeCarasScore(carasFactors),
    maxScore: 100,
    date,
    carasFactors,
  };
}

// One hand-verified value set per CARAS risk band (Very Low/Low/Medium/High/
// Very High -- see getCarasRiskLevel in RiskAssessmentSection.tsx), computed
// to land clearly inside each band rather than near a boundary.
const GENERIC_CARAS_FACTOR_VALUES_BY_BAND: Array<Array<number>> = [
  [1, 1, 45, 0, 1, 0, 0, 0, 0, 0, 0, 1], // Very Low (~7%)
  [2, 2, 36, 0, 3, 0, 2, 1, 1, 0, 0, 2], // Low (~32%)
  [2, 3, 35, 0, 4, 0, 2, 1, 2, 0, 0, 2], // Medium (~45%)
  [3, 3, 32, 0, 4, 0, 2, 1, 2, 0, 1, 3], // High (~61%)
  [4, 5, 26, 1, 5, 1, 3, 2, 3, 1, 1, 4], // Very High (~93%)
];

const RAW_HEARINGS: Array<
  Omit<ParoleHearing, "hearingDate"> & { daysFromNow: number }
> = [
  {
    pseudonymizedId: "45821",
    displayId: "945821",
    individualName: "Anderson, Michael",
    hearingType: "Parole Grant Hearing",
    facility: "Central State Correctional Facility",
    daysFromNow: 5,
  },
  {
    pseudonymizedId: "52903",
    displayId: "952903",
    individualName: "Brooks, Sarah",
    hearingType: "Parole Grant Hearing",
    facility: "North River Correctional Center",
    daysFromNow: 8,
  },
  {
    pseudonymizedId: "61247",
    displayId: "961247",
    individualName: "Chen, David",
    hearingType: "Revocation Hearing",
    facility: "Western State Prison",
    daysFromNow: 12,
  },
  {
    pseudonymizedId: "48392",
    displayId: "948392",
    individualName: "Davis, Jennifer",
    hearingType: "Parole Grant Hearing",
    facility: "Central State Correctional Facility",
    daysFromNow: 15,
  },
  {
    pseudonymizedId: "71458",
    displayId: "971458",
    individualName: "Evans, Robert",
    hearingType: "Parole Grant Hearing",
    facility: "South Bay Detention Center",
    daysFromNow: 19,
  },
  {
    pseudonymizedId: "55729",
    displayId: "955729",
    individualName: "Foster, Maria",
    hearingType: "Parole Grant Hearing",
    facility: "North River Correctional Center",
    daysFromNow: 23,
  },
  {
    pseudonymizedId: "63184",
    displayId: "963184",
    individualName: "Garcia, Carlos",
    hearingType: "Parole Grant Hearing",
    facility: "Western State Prison",
    daysFromNow: 26,
  },
  {
    pseudonymizedId: "59402",
    displayId: "959402",
    individualName: "Harris, Patricia",
    hearingType: "Modification Hearing",
    facility: "Central State Correctional Facility",
    daysFromNow: 30,
  },
];

const SHARED_HEARINGS: Array<ParoleHearing> = RAW_HEARINGS.map(
  ({ daysFromNow, ...hearing }) =>
    paroleHearingSchema.parse({
      ...hearing,
      hearingDate: iso(addDays(new Date(), daysFromNow)),
    }),
);

// Anonymized Colorado records from the CO Parole Board MVP Sample, used to
// check the profile page against realistic resident data. Names, ids, and
// other identifying fields are random, so no entry identifies a real person.
// Entries here belong only on US_CO's docket. Add each new record's hearing
// to this array, and its case-profile detail to CO_ANONYMIZED_CASE_PROFILES
// below (a resident may appear more than once here across separate
// hearings, each with its own pseudonymizedId). displayId reuses the
// pseudonymizedId value, since the sample has no separate ADCNUMBER.
const CO_HEARINGS: Array<ParoleHearing> = [
  paroleHearingSchema.parse({
    pseudonymizedId: "924893",
    displayId: "924893",
    individualName: "BANNER, BRUCE",
    hearingType: "Parole Grant Hearing",
    facility: "Fremont Correctional Facility",
    hearingDate: "2026-10-01",
  }),
  paroleHearingSchema.parse({
    pseudonymizedId: "551712",
    displayId: "551712",
    individualName: "ROGERS, STEVE",
    hearingType: "Parole Grant Hearing",
    facility: "Colorado State Penitentiary",
    hearingDate: "2026-10-01",
  }),
];

// ID-specific hand-authored records (OBT-45410), analogous to CO_HEARINGS/
// CO_ANONYMIZED_CASE_PROFILES above but entirely synthetic. Add each new
// ID-only individual's hearing here, and their case-profile detail to
// ID_SYNTHETIC_CASE_PROFILES below.
const ID_HEARINGS: Array<ParoleHearing> = [
  paroleHearingSchema.parse({
    pseudonymizedId: "350463",
    displayId: "350463",
    individualName: "STARK, TONY",
    hearingType: "Parole Grant Hearing",
    facility: "Central State Correctional Facility",
    hearingDate: iso(addDays(new Date(), 35)),
  }),
];

// Keyed by state so `nx offline staff` can serve each Parole-enabled tenant
// its own docket -- unlike the case data below (which varies only by
// severity scheme), US_CO's docket carries extra anonymized records that
// US_ID's does not.
export const paroleHearingsFixtureByState: Record<
  ParoleFixtureStateCode,
  Array<ParoleHearing>
> = {
  US_ID: [...SHARED_HEARINGS, ...ID_HEARINGS],
  US_CO: [...SHARED_HEARINGS, ...CO_HEARINGS],
};

// US_CO has no Parole backend yet (TODO(OBT-41775): replace this fixture with
// real data once one exists). Case-profile detail for each individual on the
// docket above, keyed by pseudonymizedId. Anderson's case is hand-authored to match the
// OBT-41624 design mock 1:1; the rest are generically derived from their
// docket row so every docket link resolves to a valid profile. Harris (below)
// has no hearing scheduled on her case profile -- despite appearing on the
// docket -- so the profile page's "not scheduled" / no-badge states have
// fixture coverage. Brooks and Chen are similarly steered to cover the
// Attachments section's stale-parole-plan and no-parole-plan-on-file banners
// (see STALE_PAROLE_PLAN_PSEUDONYMIZED_ID / NO_PAROLE_PLAN_PSEUDONYMIZED_ID below), matching the
// same two individuals used for this in the Parole POC.
const HEARING_TIME_BY_PSEUDONYMIZED_ID: Record<string, string> = {
  "52903": "10:30 AM",
  "61247": "1:00 PM",
  "48392": "9:30 AM",
  "71458": "11:00 AM",
  "55729": "2:00 PM",
  "63184": "10:00 AM",
};

// Keyed by pseudonymizedId (rather than derived from `individualName`) so it stays
// correct even if a fixture name is ever reworded.
const GENDER_BY_PSEUDONYMIZED_ID: Record<string, string> = {
  "45821": "Male", // Anderson, Michael
  "52903": "Female", // Brooks, Sarah
  "61247": "Male", // Chen, David
  "48392": "Female", // Davis, Jennifer
  "71458": "Male", // Evans, Robert
  "55729": "Female", // Foster, Maria
  "63184": "Male", // Garcia, Carlos
  "59402": "Female", // Harris, Patricia
};

const NO_HEARING_SCHEDULED_PSEUDONYMIZED_ID = "59402";

// Brooks: parole plan on file, but not updated in over 90 days.
const STALE_PAROLE_PLAN_PSEUDONYMIZED_ID = "52903";
// Chen: no parole plan on file at all.
const NO_PAROLE_PLAN_PSEUDONYMIZED_ID = "61247";
// Chen is also the fixture's parole-return case, so the General Info banner
// has coverage alongside his other flagged states above.
const PAROLE_RETURN_PSEUDONYMIZED_ID = "61247";

const GENERIC_CASE_MANAGER_NAMES = [
  "David Thompson",
  "Robert Johnson",
  "Maria Gonzalez",
  "Kevin Park",
  "Angela Wright",
  "Brian Lee",
  "Nicole Adams",
];

const GENERIC_ATTACHMENT_AUTHORS = [
  "Family Member",
  "Employer",
  "Community Sponsor",
  "Clergy",
  "Case Worker",
];

const GENERIC_COMMUNITY_SUPERVISION_SPONSORS: Array<
  Omit<ParoleCase["communitySupervisionPlan"][number], "recommended">
> = [
  {
    typeOfPlan: "Regular Parole Plan - Planned Residence after Release",
    name: "Patricia Nguyen",
    relationship: "Mother",
    address: "118 Birchwood Dr, Aurora, CO 80010",
  },
  {
    typeOfPlan: "Regular Parole Plan - Planned Residence after Release",
    name: "Marcus Fields",
    relationship: "Brother",
    address: "77 Canyon Ridge Rd, Pueblo, CO 81003",
  },
  {
    typeOfPlan: "Alternate Plan - Sober Living Facility",
    name: "New Horizons Recovery House",
    relationship: "Organization",
    address: "540 Elm St, Grand Junction, CO 81501",
  },
];

const GENERIC_COMMUNITY_SUPERVISION_RECOMMENDATIONS: ReadonlyArray<
  ParoleCase["communitySupervisionPlan"][number]["recommended"]
> = ["YES (Favorable)", "Pending", "TBD", "Withdrawn", "NO (Unfavorable)"];

const CUSTODY_LEVELS = ["Minimum", "Medium", "Maximum"] as const;

// Institutional conduct records, keyed by how many months before the module
// loads they occurred (see the `iso`/relative-date rationale above). Anderson
// is hand-authored to match the OBT-41634 design mock 1:1 -- six records
// split 4 Class A / 2 Class C, with the two most recent falling within the past
// year and the rest older, so the mock's "2 shown, 4 under 'See Older
// Disciplinaries'" split always renders as designed.
function buildConductRecord(
  monthsAgo: number,
  fields: Omit<ParoleConductRecord, "date">,
): ParoleConductRecord {
  return paroleConductRecordSchema.parse({
    ...fields,
    date: iso(subMonths(new Date(), monthsAgo)),
  });
}

// Each record's severity varies by state -- CO uses its Class 1/2/3 scheme,
// ID uses Class A/B/C, as Atlas spells them -- but the underlying incident
// (facility, violation, description, disposition) is the same fixture "story"
// either way.
const ANDERSON_CONDUCT_RECORDS: Array<{
  monthsAgo: number;
  facility: string;
  violation: string;
  description: string;
  disposition: string;
  isPending: boolean;
  severityByState: Record<ParoleFixtureStateCode, string>;
}> = [
  {
    monthsAgo: 0,
    facility: "Western State Prison",
    violation: "Refusal to Submit to Drug Test",
    description:
      "Refused random urinalysis screening without valid medical exemption.",
    disposition: "30 days disciplinary segregation, loss of good time",
    isPending: true,
    severityByState: { US_CO: "Class 1", US_ID: "Class A" },
  },
  {
    monthsAgo: 2,
    facility: "Western State Prison",
    violation: "Unauthorized Area",
    description:
      "Found in restricted maintenance corridor without authorization.",
    disposition: "Loss of privileges - 7 days",
    isPending: false,
    severityByState: { US_CO: "Class 3", US_ID: "Class C" },
  },
  {
    monthsAgo: 14,
    facility: "Western State Prison",
    violation: "Threatening Behavior",
    description: "Verbal threats toward staff member.",
    disposition: "30 days disciplinary segregation, anger management referral",
    isPending: false,
    severityByState: { US_CO: "Class 2", US_ID: "Class A" },
  },
  {
    monthsAgo: 21,
    facility: "Western State Prison",
    violation: "Fighting",
    description: "Physical altercation in dining hall.",
    disposition: "45 days disciplinary segregation",
    isPending: false,
    severityByState: { US_CO: "Class 1", US_ID: "Class A" },
  },
  {
    monthsAgo: 29,
    facility: "Western State Prison",
    violation: "Disobeying Orders",
    description: "Refused work assignment.",
    disposition: "Loss of privileges - 14 days",
    isPending: false,
    severityByState: { US_CO: "Class 3", US_ID: "Class C" },
  },
  {
    monthsAgo: 34,
    facility: "Western State Prison",
    violation: "Possession of Contraband",
    description: "Found with an unauthorized cell phone during a cell search.",
    disposition: "60 days disciplinary segregation, loss of good time",
    isPending: false,
    severityByState: { US_CO: "Class 2", US_ID: "Class A" },
  },
];

function buildAndersonConductHistory(
  stateCode: ParoleFixtureStateCode,
): Array<ParoleConductRecord> {
  return ANDERSON_CONDUCT_RECORDS.map(
    ({ monthsAgo, severityByState, ...fields }) =>
      buildConductRecord(monthsAgo, {
        ...fields,
        severity: severityByState[stateCode],
      }),
  );
}

// Generic docket entries cycle through three conduct patterns by index so
// the profile page's empty state, single-record state, and "See Older
// Disciplinaries" toggle all get exercised across the fixture docket without
// hand-authoring every case.
function buildGenericConductHistory(
  index: number,
  facility: string,
  stateCode: ParoleFixtureStateCode,
): Array<ParoleConductRecord> {
  const classOne = stateCode === "US_CO" ? "Class 1" : "Class A";
  const classThree = stateCode === "US_CO" ? "Class 3" : "Class C";

  const pattern = index % 3;
  if (pattern === 0) return [];
  if (pattern === 1) {
    return [
      buildConductRecord(1, {
        facility,
        violation: "Failure to Report",
        description: "Missed scheduled headcount.",
        severity: classThree,
        disposition: "Loss of privileges - 3 days",
        isPending: false,
      }),
    ];
  }
  return [
    buildConductRecord(1, {
      facility,
      violation: "Insubordination",
      description: "Refused a direct order from a correctional officer.",
      severity: classOne,
      disposition: "14 days disciplinary segregation",
      isPending: false,
    }),
    buildConductRecord(16, {
      facility,
      violation: "Unauthorized Area",
      description: "Found in a restricted area without authorization.",
      severity: classThree,
      disposition: "Loss of privileges - 7 days",
      isPending: false,
    }),
  ];
}

const RISK_NEED_FACTORS = [
  "Medical",
  "Dental",
  "Mental Health",
  "ID",
  "Sex Offender",
  "Substance Abuse Rating",
  "SOA-R Level",
];

function scaleForRiskNeedScore(score: number): ParoleRiskNeedScale {
  if (score <= 1) return "Low";
  if (score <= 3) return "Moderate";
  return "High";
}

// Mental Health's real eOMIS score is qualifier-suffixed (e.g. "3/M"), unlike
// every other factor's plain integer display.
function formatRiskNeedScore(factor: string, score: number): string {
  return factor === "Mental Health" ? `${score}/M` : `${score}`;
}

// No real US_CO backend exists for this yet (TODO(OBT-41775), same as the
// rest of this file), so scores are deterministically varied by index/factor
// position rather than hand-authored or truly randomized -- keeps `nx
// offline staff` and tests reproducible while still cycling through the
// full 0-5 range across residents.
function buildRiskAndNeedsFactors(index: number): Array<ParoleRiskNeedFactor> {
  return RISK_NEED_FACTORS.map((factor, factorIndex) => {
    const score = (index * 7 + factorIndex * 3) % 6;
    return {
      factor,
      score: formatRiskNeedScore(factor, score),
      scale: scaleForRiskNeedScore(score),
    };
  });
}

function buildAndersonCaseProfile(
  hearingDate: string,
  stateCode: ParoleFixtureStateCode,
): ParoleCase {
  const today = new Date();
  // LSI/PIT/SRT/CARAS each have a fixed most-recent assessment date (matching
  // verified real assessment dates, OBT-43413) rather than one relative to
  // `today` like the rest of this file. Their earlier history points are
  // anchored to that same fixed date -- not to `today` -- so they're
  // guaranteed to stay chronologically before it forever, regardless of how
  // far `today` drifts forward; anchoring to `today` instead would eventually
  // put a "historical" point after the fixed date it's supposed to precede.
  const lsiPitAnchor = parseISO("2025-06-26");
  const srtAnchor = parseISO("2026-03-19");
  const carasAnchor = parseISO("2026-04-16");
  return paroleCaseSchema.parse({
    pseudonymizedId: "45821",
    displayId: "945821",
    name: "Anderson, Michael",
    dob: iso(subYears(today, 40)),
    gender: GENDER_BY_PSEUDONYMIZED_ID["45821"],
    currentFacility: "Central State Correctional Facility",
    custodyLevel: "Minimum",
    caseManagerName: "Jennifer Martinez",
    hearingDate,
    hearingTime: "9:00 AM",
    hearingType: "Parole Grant Hearing",
    isParoleReturn: false,
    sentenceStartDate: iso(subYears(today, 4)),
    paroleEligibilityDate: iso(addDays(today, 20)),
    mandatoryReleaseDate: iso(addDays(today, 700)),
    parolePlan: {
      onFile: true,
      lastUpdated: iso(subDays(today, 20)),
      documents: [
        {
          url: "/documents/parole-plan-45821-v1.pdf",
          uploadDate: iso(subDays(today, 20)),
        },
        {
          url: "/documents/parole-plan-45821-v2.pdf",
          uploadDate: iso(subDays(today, 25)),
        },
      ],
    },
    attachments: [
      {
        name: "Letter of Support - Rev. Thomas Mills",
        type: "Letter of Support",
        url: "/documents/support-letter-mills-45821.pdf",
        uploadDate: iso(subDays(today, 22)),
      },
      {
        name: "Letter of Support - Mary Anderson (Sister)",
        type: "Letter of Support",
        url: "/documents/support-letter-anderson-45821.pdf",
        uploadDate: iso(subDays(today, 24)),
      },
      {
        name: "Victim Impact Statement",
        type: "Victim Impact Letter",
        url: "/documents/victim-impact-45821.pdf",
        uploadDate: iso(subDays(today, 40)),
      },
    ],
    conductHistory: buildAndersonConductHistory(stateCode),
    riskAndNeedsFactors: buildRiskAndNeedsFactors(0),
    communitySupervisionPlan: [
      {
        typeOfPlan: "Regular Parole Plan - Planned Residence after Release",
        name: "Robert Anderson",
        relationship: "Father",
        address: "412 Maple Ave, Denver, CO 80204",
        recommended: "YES (Favorable)",
      },
      {
        typeOfPlan: "Alternate Plan - Sober Living Facility",
        name: "Hazelbrook Sober Living",
        relationship: "Organization",
        address: "329 S 18th Street, Suite 3, Colorado Springs, CO 80904",
        recommended: "Pending",
      },
    ],
    // US_ID-only; CO's profile never renders it. Formatted like the backend:
    // one dated note per line, newest first.
    disciplinaryFacilityNotes:
      "03/14/2025: Resident accepted responsibility and completed the assigned sanction without incident.\n08/02/2023: Resident has remained compliant with facility programming since the hearing.",
    // US_ID-only, formatted like the backend: group names joined by "; ".
    // Only this Anderson-derived case sets it, so the hardcoded US_ID cases
    // exercise the empty state.
    stg: "Northsiders; Westside Crew",
    // Full assessment history per tool -- the most recent entry per tool
    // carries the real subcategory/CARAS-factor breakdown; earlier entries
    // are bare score/date pairs, matching what a real historical record
    // would actually have on file. The trajectory chart and the "current"
    // per-tool detail are both derived from this single array (see
    // RiskAssessmentSection.utils.ts), so there's nothing else to keep in
    // sync.
    riskAssessments: [
      // LSI: fixed most-recent date (matching a verified real assessment,
      // OBT-43413), so its history is anchored to `lsiPitAnchor` rather than
      // `today`.
      {
        tool: "LSIR",
        score: 32,
        maxScore: 54,
        date: iso(subYears(lsiPitAnchor, 3)),
      },
      {
        tool: "LSIR",
        score: 24,
        maxScore: 54,
        date: iso(subYears(lsiPitAnchor, 2)),
      },
      {
        tool: "LSIR",
        score: 17,
        maxScore: 54,
        date: iso(subMonths(lsiPitAnchor, 2)),
      },
      {
        tool: "LSIR",
        score: 31,
        maxScore: 54,
        date: "2025-06-26",
        subcategories: [
          { name: "Criminal History", score: 9, maxScore: 10 },
          { name: "Education/Employment", score: 7, maxScore: 10 },
          { name: "Financial", score: 1, maxScore: 2 },
          { name: "Family/Marital", score: 3, maxScore: 4 },
          { name: "Accommodation", score: 1, maxScore: 3 },
          { name: "Leisure/Recreation", score: 2, maxScore: 2 },
          { name: "Companions", score: 4, maxScore: 5 },
          { name: "Alcohol/Drug", score: 2, maxScore: 9 },
          { name: "Emotional/Personal", score: 0, maxScore: 5 },
          { name: "Attitude/Orientation", score: 2, maxScore: 4 },
        ],
      },
      // PIT: same verified assessment day as LSI, so it shares `lsiPitAnchor`.
      {
        tool: "PIT",
        score: 20,
        maxScore: 39,
        date: iso(subYears(lsiPitAnchor, 3)),
      },
      {
        tool: "PIT",
        score: 12,
        maxScore: 39,
        date: iso(subYears(lsiPitAnchor, 2)),
      },
      {
        tool: "PIT",
        score: 14,
        maxScore: 39,
        date: iso(subMonths(lsiPitAnchor, 2)),
      },
      {
        tool: "PIT",
        score: 28,
        maxScore: 39,
        date: "2025-06-26",
        subcategories: [
          { name: "Criminal History", score: 9, maxScore: 10 },
          {
            name: "Education, Employment, and Financial Situation",
            score: 7,
            maxScore: 6,
          },
          { name: "Family and Social Support", score: 4, maxScore: 6 },
          {
            name: "Substance Abuse and Mental Health",
            score: 1,
            maxScore: 5,
          },
          {
            name: "Criminal Attitudes and Behavioral Patterns",
            score: 7,
            maxScore: 11,
          },
        ],
      },
      // CARAS: fixed most-recent date (matching the reference CARAS v7
      // sample assessment), so its history is anchored to `carasAnchor`.
      {
        tool: "CARAS",
        score: 52,
        maxScore: 100,
        date: iso(subYears(carasAnchor, 3)),
      },
      {
        tool: "CARAS",
        score: 40,
        maxScore: 100,
        date: iso(subMonths(carasAnchor, 18)),
      },
      {
        tool: "CARAS",
        score: 26,
        maxScore: 100,
        date: iso(subMonths(carasAnchor, 6)),
      },
      {
        tool: "CARAS",
        score: 16,
        maxScore: 100,
        date: iso(subMonths(carasAnchor, 1)),
      },
      buildCarasAssessment([1, 3, 44, 0, 4, 0, 1, 0, 0, 0, 0, 1], "2026-04-16"),
      // SRT: fixed most-recent date, anchored to `srtAnchor`.
      {
        tool: "SRT",
        score: 18,
        maxScore: 44,
        date: iso(subYears(srtAnchor, 2)),
      },
      {
        tool: "SRT",
        score: 12,
        maxScore: 44,
        date: iso(subMonths(srtAnchor, 9)),
      },
      {
        tool: "SRT",
        score: 30,
        maxScore: 44,
        date: "2026-03-19",
        subcategories: [
          { name: "Criminal History", score: 11, maxScore: 12 },
          {
            name: "Education, Employment, and Financial Situation",
            score: 8,
            maxScore: 9,
          },
          {
            name: "Substance Abuse and Mental Health",
            score: 3,
            maxScore: 4,
          },
          {
            name: "Criminal Attitudes and Behavioral Patterns",
            score: 8,
            maxScore: 19,
          },
        ],
      },
      // Placeholder data (OBT-43413) -- US_CO has no real RT/CST source yet,
      // so these mirror the shape of the verified LSI/PIT/SRT data above
      // rather than real assessment values. RT/CST have no fixed reference
      // date, so -- unlike LSI/PIT/SRT/CARAS above -- their history is
      // anchored to `today` throughout, which keeps every entry for a given
      // tool consistently ordered relative to each other no matter when
      // `today` is.
      { tool: "RT", score: 16, maxScore: 28, date: iso(subMonths(today, 10)) },
      {
        tool: "RT",
        score: 12,
        maxScore: 28,
        date: iso(subMonths(today, 4)),
        subcategories: [
          { name: "Criminal History", score: 5, maxScore: 10 },
          { name: "Employment/Education", score: 4, maxScore: 10 },
          { name: "Social Support", score: 3, maxScore: 8 },
        ],
      },
      { tool: "CST", score: 22, maxScore: 49, date: iso(subMonths(today, 6)) },
      {
        tool: "CST",
        score: 18,
        maxScore: 49,
        date: iso(subMonths(today, 2)),
        subcategories: [
          { name: "Criminal History", score: 6, maxScore: 15 },
          { name: "Employment/Education", score: 5, maxScore: 14 },
          { name: "Substance Abuse", score: 4, maxScore: 10 },
          { name: "Social Support", score: 3, maxScore: 10 },
        ],
      },
    ],
    docPrograms: [
      {
        name: "Cognitive Behavioral Therapy",
        referralDate: iso(subMonths(today, 11)),
        startDate: iso(subMonths(today, 10)),
        completionDate: iso(subMonths(today, 4)),
        type: "Treatment",
        criminogenicNeed: "Antisocial Thinking",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Substance Abuse Treatment",
        referralDate: iso(subMonths(today, 14)),
        startDate: iso(subMonths(today, 13)),
        completionDate: iso(subMonths(today, 7)),
        type: "Treatment",
        criminogenicNeed: "Substance Abuse",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Vocational Training - Welding",
        referralDate: iso(subMonths(today, 3)),
        startDate: iso(subMonths(today, 2)),
        completionDate: null,
        type: "Education/Vocational",
        criminogenicNeed: "Employment",
        status: "IN_PROGRESS",
      },
      {
        // Waitlisted, so it has a referral but never started -- the case the
        // Programming table's referral-date fallback exists for.
        name: "Anger Management",
        referralDate: iso(subMonths(today, 1)),
        completionDate: null,
        type: "Treatment",
        criminogenicNeed: "Antisocial Thinking",
        status: "PENDING",
      },
      {
        name: "GED Preparation",
        referralDate: iso(subMonths(today, 20)),
        startDate: iso(subMonths(today, 19)),
        completionDate: iso(subMonths(today, 16)),
        type: "Education/Vocational",
        criminogenicNeed: "Education",
        status: "DISCHARGED_UNSUCCESSFUL",
      },
    ],
    edovoPrograms: [
      {
        title: "Financial Literacy Basics",
        completionDate: iso(subMonths(today, 6)),
        status: "completed",
        result: "passed",
        startDate: iso(subMonths(today, 7)),
        durationDays: 28,
      },
      {
        title: "Resume Building Workshop",
        completionDate: iso(subMonths(today, 5)),
        status: "completed",
        result: "passed",
        startDate: iso(subMonths(today, 6)),
        durationDays: 13,
      },
      {
        title: "Mindfulness and Stress Management",
        completionDate: null,
        status: "in-progress",
        startDate: iso(subMonths(today, 2)),
      },
    ],
    offenseHistory: {
      offenses: [
        {
          county: "Sangamon County",
          docket: "2021-CF-0489",
          conviction: "Armed Robbery",
          classFelony: "Class X Felony",
          sentence: "8 years",
          dateOfOffense: iso(subYears(today, 5)),
          convictionDate: iso(subYears(today, 4)),
          offenseNarrative:
            "Defendant entered convenience store with firearm and demanded cash from register. No injuries occurred. Defendant apprehended two blocks from scene.",
          statute: "18-6501",
          sentencingDate: iso(subYears(today, 4)),
          sentenceStartDate: iso(subYears(today, 4)),
          paroleEligibilityDate: iso(subMonths(today, 6)),
          fullTermDate: iso(addYears(today, 4)),
          indeterminateStartDate: iso(subMonths(today, 6)),
          indeterminateEndDateInclusive: iso(addYears(today, 4)),
        },
        {
          county: "Sangamon County",
          docket: "2021-CF-0490",
          conviction:
            "Possession of a Controlled Substance With Intent to Deliver",
          classFelony: "Class 2 Felony",
          sentence: "5 years",
          dateOfOffense: iso(subYears(today, 5)),
          convictionDate: iso(subYears(today, 4)),
          offenseNarrative:
            "Defendant was found in possession of a controlled substance in a quantity indicating intent to distribute.",
          statute: "37-2732(a)",
          sentencingDate: iso(subYears(today, 4)),
          sentenceStartDate: iso(subYears(today, 4)),
          paroleEligibilityDate: iso(subMonths(today, 6)),
          fullTermDate: iso(addYears(today, 4)),
          // A determinate sentence: no indeterminate portion to serve, so
          // Atlas sends neither bound and the card shows the placeholder.
        },
        {
          county: "Sangamon County",
          docket: "2021-CF-0491",
          conviction: "Escape",
          classFelony: "Class 4 Felony",
          sentence: "2 years",
          dateOfOffense: iso(subYears(today, 5)),
          convictionDate: iso(subYears(today, 4)),
          offenseNarrative:
            "Defendant left a work-release assignment without authorization and was apprehended the following day.",
          statute: "18-2505",
          sentencingDate: iso(subYears(today, 2)),
          sentenceStartDate: iso(subYears(today, 2)),
          paroleEligibilityDate: iso(addYears(today, 4)),
          fullTermDate: iso(addYears(today, 5)),
          indeterminateStartDate: iso(addYears(today, 4)),
          indeterminateEndDateInclusive: iso(addYears(today, 5)),
        },
      ],
      priorConvictions: [
        { charge: "Theft", date: iso(subYears(today, 8)) },
        { charge: "Assault", date: iso(subYears(today, 7)) },
      ],
      victimInvolved: true,
      victimAttendingHearing: true,
    },
  });
}

function buildParolePlan(
  pseudonymizedId: string,
  index: number,
  today: Date,
): ParoleCase["parolePlan"] {
  if (pseudonymizedId === NO_PAROLE_PLAN_PSEUDONYMIZED_ID) {
    return { onFile: false, documents: [] };
  }

  const lastUpdated = iso(
    subDays(
      today,
      pseudonymizedId === STALE_PAROLE_PLAN_PSEUDONYMIZED_ID
        ? 130
        : 10 + index * 4,
    ),
  );
  return {
    onFile: true,
    lastUpdated,
    documents: [
      {
        url: `/documents/parole-plan-${pseudonymizedId}.pdf`,
        uploadDate: lastUpdated,
      },
    ],
  };
}

function buildAttachments(
  pseudonymizedId: string,
  index: number,
  today: Date,
): ParoleCase["attachments"] {
  const author =
    GENERIC_ATTACHMENT_AUTHORS[index % GENERIC_ATTACHMENT_AUTHORS.length];
  return [
    {
      name: `Letter of Support - ${author}`,
      type: "Letter of Support",
      url: `/documents/support-letter-${pseudonymizedId}.pdf`,
      uploadDate: iso(subDays(today, 5 + index * 3)),
    },
  ];
}

function buildCommunitySupervisionPlan(
  index: number,
): ParoleCase["communitySupervisionPlan"] {
  const sponsor =
    GENERIC_COMMUNITY_SUPERVISION_SPONSORS[
      index % GENERIC_COMMUNITY_SUPERVISION_SPONSORS.length
    ];
  const recommended =
    GENERIC_COMMUNITY_SUPERVISION_RECOMMENDATIONS[
      index % GENERIC_COMMUNITY_SUPERVISION_RECOMMENDATIONS.length
    ];
  return [{ ...sponsor, recommended }];
}

// US_ID's real assessment tools (OBT-45410) -- distinct from US_CO's
// LSI/PIT/CARAS/SRT set below. Each tool past LSI is only "on file" for a
// subset of the generic docket (via `index`), so the sidebar Assessments
// list's "Non Applicable/Not on File" state has real demo coverage.
/**
 * The LSI-R's ten published domains and their item counts, which add up to
 * the tool's 54-point total. Same names and maxima US_CO's fixtures use, so
 * the two states spell the domains one way.
 */
const LSIR_DOMAINS = [
  { name: "Criminal History", maxScore: 10 },
  { name: "Education/Employment", maxScore: 10 },
  { name: "Financial", maxScore: 2 },
  { name: "Family/Marital", maxScore: 4 },
  { name: "Accommodation", maxScore: 3 },
  { name: "Leisure/Recreation", maxScore: 2 },
  { name: "Companions", maxScore: 5 },
  { name: "Alcohol/Drug", maxScore: 9 },
  { name: "Emotional/Personal", maxScore: 5 },
  { name: "Attitude/Orientation", maxScore: 4 },
] as const;

/**
 * STATIC-99's ten items, each scored 0-1 except prior sex offences, adding
 * up to the tool's 12-point total.
 */
const STATIC_99_DOMAINS = [
  { name: "Young Age at Release", maxScore: 1 },
  { name: "Ever Lived With Partner", maxScore: 1 },
  { name: "Index Non-Sexual Violence", maxScore: 1 },
  { name: "Prior Non-Sexual Violence", maxScore: 1 },
  { name: "Prior Sex Offences", maxScore: 3 },
  { name: "Prior Sentencing Dates", maxScore: 1 },
  { name: "Non-Contact Sex Offences", maxScore: 1 },
  { name: "Unrelated Victims", maxScore: 1 },
  { name: "Stranger Victims", maxScore: 1 },
  { name: "Male Victims", maxScore: 1 },
] as const;

/**
 * STABLE-2007's five domains. Its thirteen items each score 0-2, so a
 * domain's max is twice its item count, and the five add up to 26.
 */
const STABLE_DOMAINS = [
  { name: "Significant Social Influences", maxScore: 2 },
  { name: "Intimacy Deficits", maxScore: 10 },
  { name: "Sexual Self-Regulation", maxScore: 6 },
  { name: "Cooperation With Supervision", maxScore: 2 },
  { name: "General Self-Regulation", maxScore: 6 },
] as const;

/**
 * VRAG's twelve published items. The real tool weights each item by a
 * regression coefficient that can be negative, rather than adding them up,
 * so these maxima are a fixture-only approximation chosen to total 38 -- a
 * part-of-whole bar is the wrong shape for how VRAG really scores.
 * TODO(OBT-50421): drop these once the backend sends real per-item data, and
 * render VRAG through carasFactors instead.
 */
const VRAG_DOMAINS = [
  { name: "Lived With Both Parents to Age 16", maxScore: 3 },
  { name: "Elementary School Maladjustment", maxScore: 5 },
  { name: "History of Alcohol Problems", maxScore: 2 },
  { name: "Marital Status", maxScore: 2 },
  { name: "Nonviolent Offence History", maxScore: 3 },
  { name: "Failure on Prior Conditional Release", maxScore: 3 },
  { name: "Age at Index Offence", maxScore: 5 },
  { name: "Victim Injury", maxScore: 2 },
  { name: "Female Victim", maxScore: 1 },
  { name: "Personality Disorder", maxScore: 3 },
  { name: "Schizophrenia", maxScore: 3 },
  { name: "Psychopathy Checklist Score", maxScore: 6 },
] as const;

type ScoredDomain = { name: string; maxScore: number };

/**
 * Splits a total across a tool's domains, so the parts add back up to the
 * score shown beside them. Each domain takes its proportional share, varied
 * by the case's index so two residents don't show the same shape, and any
 * rounding drift is absorbed by the domains that have room for it.
 *
 * @param domains - The tool's domains and their maxima.
 * @param score - The assessment's total score.
 * @param index - The case's position in the fixture list.
 */
function buildSubcategories(
  domains: ReadonlyArray<ScoredDomain>,
  score: number,
  index: number,
): Array<ParoleSubcategoryScore> {
  const total = domains.reduce((sum, d) => sum + d.maxScore, 0);
  const subcategories = domains.map((domain, domainIndex) => ({
    name: domain.name,
    score: Math.max(
      0,
      Math.min(
        domain.maxScore,
        Math.round((score / total) * domain.maxScore) +
          (((index + domainIndex) % 3) - 1),
      ),
    ),
    maxScore: domain.maxScore,
  }));

  let drift = score - subcategories.reduce((sum, s) => sum + s.score, 0);
  for (const subcategory of subcategories) {
    if (drift === 0) break;
    const room =
      drift > 0 ? subcategory.maxScore - subcategory.score : -subcategory.score;
    const step = drift > 0 ? Math.min(drift, room) : Math.max(drift, room);
    subcategory.score += step;
    drift -= step;
  }
  return subcategories;
}

function buildIdRiskAssessments(
  index: number,
  today: Date,
): Pick<ParoleCase, "riskAssessments"> {
  const riskPct = 20 + ((index * 17) % 60); // varies 20-79%
  const lsirScore = Math.round((riskPct / 100) * 54);

  // Every tool with published items gets a breakdown. The LSI-R, STATIC-99
  // and STABLE-2007 score theirs additively out of a max, which is exactly
  // what the chart draws; VRAG's are approximated (see VRAG_DOMAINS).
  const riskAssessments: Array<ParoleRiskAssessment> = [
    {
      tool: "LSIR",
      score: lsirScore,
      maxScore: 54,
      date: iso(subMonths(today, 4)),
      subcategories: buildSubcategories(LSIR_DOMAINS, lsirScore, index),
    },
  ];
  if (index % 3 !== 0) {
    const vragScore = Math.round((riskPct / 100) * 38);
    riskAssessments.push({
      tool: "VRAG",
      score: vragScore,
      maxScore: 38,
      date: iso(subMonths(today, 5)),
      subcategories: buildSubcategories(VRAG_DOMAINS, vragScore, index),
    });
  }
  if (index % 4 !== 0) {
    const staticScore = Math.round((riskPct / 100) * 12);
    riskAssessments.push({
      tool: "STATIC_99",
      score: staticScore,
      maxScore: 12,
      date: iso(subMonths(today, 6)),
      subcategories: buildSubcategories(STATIC_99_DOMAINS, staticScore, index),
    });
  }
  if (index % 2 === 0) {
    const stableScore = Math.round((riskPct / 100) * 26);
    riskAssessments.push({
      tool: "STABLE",
      score: stableScore,
      maxScore: 26,
      date: iso(subMonths(today, 3)),
      subcategories: buildSubcategories(STABLE_DOMAINS, stableScore, index),
    });
  }

  return { riskAssessments };
}

function buildGenericRiskAssessments(
  index: number,
  today: Date,
  stateCode: ParoleFixtureStateCode,
): Pick<ParoleCase, "riskAssessments"> {
  if (stateCode === "US_ID") return buildIdRiskAssessments(index, today);

  const riskPct = 20 + ((index * 17) % 60); // varies 20-79%
  return {
    riskAssessments: [
      {
        tool: "LSIR",
        score: Math.round((riskPct / 100) * 54),
        maxScore: 54,
        date: iso(subMonths(today, 4)),
      },
      {
        tool: "PIT",
        score: Math.round((riskPct / 100) * 40),
        maxScore: 40,
        date: iso(subMonths(today, 6)),
      },
      buildCarasAssessment(
        // Hand-tuned (not a smooth formula of `index`) so the generic cases
        // cycle through all 5 CARAS risk bands rather than clustering in
        // just one or two -- useful for demoing the full risk-level range.
        GENERIC_CARAS_FACTOR_VALUES_BY_BAND[index % 5],
        iso(subMonths(today, 8)),
      ),
      {
        tool: "SRT",
        score: Math.round((riskPct / 100) * 25),
        maxScore: 25,
        date: iso(subMonths(today, 10)),
      },
    ],
  };
}

function buildOffenseHistory(
  index: number,
  today: Date,
): ParoleCase["offenseHistory"] {
  return {
    offenses: [
      {
        county: "Sample County",
        docket: `2022-CF-0${100 + index}`,
        conviction: "Burglary",
        classFelony: "Class 2 Felony",
        sentence: "6 years",
        dateOfOffense: iso(subYears(today, 4)),
        convictionDate: iso(subYears(today, 3 + (index % 4))),
        offenseNarrative:
          "Defendant entered an unoccupied residence and removed property without consent.",
        statute: "18-1401",
        sentencingDate: iso(subYears(today, 3 + (index % 4))),
        sentenceStartDate: iso(subYears(today, 3 + (index % 4))),
        paroleEligibilityDate: iso(subMonths(today, 3)),
        fullTermDate: iso(addYears(today, 3)),
        indeterminateStartDate: iso(subMonths(today, 3)),
        indeterminateEndDateInclusive: iso(addYears(today, 3)),
      },
      // Every fifth resident also carries a life sentence, so the card's
      // "Life" rows have offline coverage. A life sentence has no full term
      // or indeterminate end -- that is what `isLife` explains.
      ...(index % 5 === 0
        ? [
            {
              county: "Sample County",
              docket: `2019-CF-0${200 + index}`,
              conviction: "Murder in the First Degree",
              classFelony: "Class A Felony",
              sentence: "Life",
              dateOfOffense: iso(subYears(today, 9)),
              convictionDate: iso(subYears(today, 8)),
              offenseNarrative:
                "Defendant was convicted following a jury trial.",
              statute: "18-4003",
              isLife: true,
              sentencingDate: iso(subYears(today, 8)),
              sentenceStartDate: iso(subYears(today, 8)),
              paroleEligibilityDate: iso(addYears(today, 2)),
              indeterminateStartDate: iso(addYears(today, 2)),
            },
          ]
        : []),
    ],
    // No prior convictions for the generic cases -- Anderson's hand-authored
    // profile above is the one that exercises the "Prior Convictions" list.
    priorConvictions: [],
    victimInvolved: false,
    victimAttendingHearing: false,
  };
}

function buildGenericCaseProfile(
  hearing: ParoleHearing,
  index: number,
  stateCode: ParoleFixtureStateCode,
): ParoleCase {
  const today = new Date();
  const hasScheduledHearing =
    hearing.pseudonymizedId !== NO_HEARING_SCHEDULED_PSEUDONYMIZED_ID;
  // Harris also anchors the "no disciplinary infractions" empty state, so her
  // conduct history is deliberately empty rather than pattern-derived.
  const conductHistory = hasScheduledHearing
    ? buildGenericConductHistory(index, hearing.facility, stateCode)
    : [];
  return paroleCaseSchema.parse({
    pseudonymizedId: hearing.pseudonymizedId,
    displayId: hearing.displayId,
    name: hearing.individualName,
    dob: iso(subYears(today, 30 + index)),
    gender: GENDER_BY_PSEUDONYMIZED_ID[hearing.pseudonymizedId],
    currentFacility: hearing.facility,
    custodyLevel: CUSTODY_LEVELS[index % CUSTODY_LEVELS.length],
    caseManagerName:
      GENERIC_CASE_MANAGER_NAMES[index % GENERIC_CASE_MANAGER_NAMES.length],
    hearingDate: hasScheduledHearing ? hearing.hearingDate : undefined,
    hearingTime: hasScheduledHearing
      ? HEARING_TIME_BY_PSEUDONYMIZED_ID[hearing.pseudonymizedId]
      : undefined,
    hearingType: hearing.hearingType,
    isParoleReturn: hearing.pseudonymizedId === PAROLE_RETURN_PSEUDONYMIZED_ID,
    sentenceStartDate: iso(subYears(today, 3 + (index % 4))),
    paroleEligibilityDate: iso(addDays(today, 10 + index * 5)),
    conductHistory,
    mandatoryReleaseDate: iso(addDays(today, 600 + index * 30)),
    parolePlan: buildParolePlan(hearing.pseudonymizedId, index, today),
    attachments: buildAttachments(hearing.pseudonymizedId, index, today),
    communitySupervisionPlan: buildCommunitySupervisionPlan(index),
    // Only Anderson's profile is hand-authored with program data (see comment
    // above); the rest have none until a real backend exists.
    docPrograms: [],
    edovoPrograms: [],
    offenseHistory: buildOffenseHistory(index, today),
    riskAndNeedsFactors: buildRiskAndNeedsFactors(index),
    ...buildGenericRiskAssessments(index, today, stateCode),
  });
}

// Case-profile detail for each anonymized Colorado record in CO_HEARINGS
// above, keyed by pseudonymizedId. Hand-authored from the CO Parole Board MVP
// Sample rather than generically derived, so each profile keeps the shape
// of a real intake record while its names, ids, and other identifying
// fields are random. Add each new record's case profile here, not a new
// builder function. Fields the source spreadsheet marked "#N/A" (no data on
// file) are set to this schema's empty/no-data value for that field's type.
// criminogenicNeed is blank for every docProgram in the source records, but
// paroleDocProgramSchema requires a non-null string, so it is set to "" here
// rather than widening the shared schema for one fixture's real-world gap.
// conductHistory severities were originally transcribed as "Class 1"/
// "Class 2", which don't match any real US_CO violationCategory value
// ("Class I"/"Class IIa"/"Class IIb"/"Class III", confirmed against the
// staging sandbox). Corrected here to "Class I"/"Class IIa" as the closest
// real equivalents; this is an approximation, not re-verified against the
// original MVP Sample spreadsheet -- update it if that source
// is checked and says otherwise.
const CO_ANONYMIZED_CASE_PROFILES: Record<string, ParoleCase> = {
  "924893": paroleCaseSchema.parse({
    pseudonymizedId: "924893",
    displayId: "924893",
    name: "BANNER, BRUCE",
    dob: "1992-04-01",
    gender: "Male",
    currentFacility: "Fremont Correctional Facility",
    custodyLevel: "Medium",
    caseManagerName: "JANE WESTON",
    hearingDate: "2026-10-01",
    hearingType: "Parole Grant Hearing",
    isParoleReturn: false,
    sentenceStartDate: "2023-06-16",
    paroleEligibilityDate: "2027-01-03",
    mandatoryReleaseDate: "2033-01-03",
    parolePlan: { onFile: false, documents: [] },
    attachments: [],
    communitySupervisionPlan: [],
    conductHistory: [
      {
        date: "2025-01-14",
        facility: "Fremont Correctional Facility",
        violation: "THEFT",
        description:
          "OFFENDER WAS WITNESSED REACHING INTO A CANTEEN CRATE REMOVING AN ITEM THAT DID NOT BELONG TO THEM AND WAS LATER FOUND IN THEIR CELL.",
        severity: "Class IIa",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
    ],
    docPrograms: [
      {
        name: "Pathways - Break Through",
        completionDate: "2025-12-10",
        type: "Mental Health",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Financial Literacy",
        completionDate: "2025-08-26",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Dream Initiative",
        completionDate: "2025-08-12",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "INTRODUCTION TO COMPUTER INFORMATION SYSTEMS 52.0101",
        completionDate: "2025-07-10",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Lifeskills - Sim Venture",
        completionDate: "2025-06-19",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "MORAL RECONATION THERAPY",
        completionDate: "2025-05-06",
        type: "Mental Health",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Cert OSHA 30-Hr Haz-Gen Ind",
        completionDate: "2025-02-27",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Seven Habits of Highly Effective People",
        completionDate: "2024-10-30",
        type: "Mental Health",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Custodial Training CIP Code 190699",
        completionDate: "2024-06-06",
        type: "Educational/Vocational",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
      {
        name: "Mental Health (CBT)",
        completionDate: null,
        type: "Mental Health",
        criminogenicNeed: "",
        status: "PENDING",
      },
      {
        name: "Mental Health (IMR)",
        completionDate: null,
        type: "Mental Health",
        criminogenicNeed: "",
        status: "PENDING",
      },
      {
        name: "Mental Health (Program 04)",
        completionDate: null,
        type: "Mental Health",
        criminogenicNeed: "",
        status: "PENDING",
      },
    ],
    edovoPrograms: [],
    offenseHistory: {
      offenses: [
        {
          county: "Jefferson County",
          docket: "31CR9989",
          conviction: "Assault",
          classFelony: "Felony Class 4",
          sentence: "12 years",
          dateOfOffense: "2021-06-24",
          convictionDate: "2023-05-11",
          offenseNarrative:
            'Per PSIR, On 06/24/2021 at about 6:27 p.m., your affiant, hereby referred to as, "I" or, "Me" was dispatched to 7701 W Tower Ave for a report of a domestic. The reporting party (RP), who was later identified as Banner, Martine (DOB/09/22/1 993), reported that her husband tried to kill her by strangling her with a chord. She further reported that she had escaped and was across the street at 7700 W Tower Ave. She advised of multiple children in the residence and further stated that her husband, the suspect, is leaving in a silver Ford F150 with a temporary license plate on the back of the truck.',
        },
      ],
      priorConvictions: [],
      victimInvolved: true,
      victimAttendingHearing: false,
    },
    riskAndNeedsFactors: buildRiskAndNeedsFactors(1),
    riskAssessments: [
      { tool: "SRT", score: 8, maxScore: 44, date: "2025-01-17" },
      { tool: "SRT", score: 16, maxScore: 44, date: "2024-01-25" },
      { tool: "PIT", score: 14, maxScore: 39, date: "2023-06-22" },
    ],
  }),
  "551712": paroleCaseSchema.parse({
    pseudonymizedId: "551712",
    displayId: "551712",
    name: "ROGERS, STEVE",
    dob: "1985-10-11",
    gender: "Male",
    currentFacility: "Colorado State Penitentiary",
    custodyLevel: "Close",
    caseManagerName: "GEORGE GEORGESON",
    hearingDate: "2026-10-01",
    hearingType: "Revocation Hearing",
    isParoleReturn: true,
    sentenceStartDate: "2018-01-19",
    paroleEligibilityDate: "2027-01-07",
    mandatoryReleaseDate: "2039-12-16",
    parolePlan: {
      onFile: false,
      documents: [],
    },
    attachments: [],
    communitySupervisionPlan: [],
    conductHistory: [
      {
        date: "2025-05-20",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "UNAUTHORIZED/INCIDENTAL CONTACT",
        description: "INMATE THREW WATER ON STAFF.",
        severity: "Class IIa",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2025-05-20",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "THREATS",
        description: "INMATE MAKE THREATS TOWARDS STAFF.",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2025-05-14",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "HAZARDOUS LIQUID ASSAULT ON STAFF",
        description:
          "INMATER ATTEMTED TO CAUSE INJURY TO STAFF BY THROWING AN UNKNOWN LIQUID ON THEM.",
        severity: "Class I",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2025-05-08",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "HAZARDOUS LIQUID ASSAULT ON STAFF",
        description: "INMATE THREW HAZARDOUS LIQUID AND HIT STAFF.",
        severity: "Class I",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
      {
        date: "2025-04-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "THREATS",
        description: "INMATE MADE THREATS TOWARDS STAFF.",
        severity: "Class IIa",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
      {
        date: "2025-03-06",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "DAMAGE TO PROPERTY (OLD:>$50)",
        description:
          "INMATE DAMAGED ITEMS ASSIGNED TO HIM REQUIRING REPLACEMENT OR REPAIR. RESTITUTION $3.96 TP CSP LAUNDRY",
        severity: "Class IIa",
        disposition: "RESTITUTION",
        isPending: false,
      },
      {
        date: "2025-02-08",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "UNAUTHORIZED POSSESSION",
        description:
          "DO LT DOE HO LT NIGHT. INMATE WS FOIUND IN POSSESSION OF UNAUTHORIZED ITEMS.",
        severity: "Class IIa",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2024-10-29",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "HAZARDOUS LIQUID ASSAULT ON STAFF",
        description:
          "OFFENDER ROGERS THREW A LIQUID FECES MIXTURE AT STAFF THROUGH THE TRAY SLOT. 15 DYS RH, 30 DYS LOGT.",
        severity: "Class I",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2024-10-29",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "HAZARDOUS LIQUID ASSAULT ON STAFF",
        description:
          "OFFENDER ROGERS THREW A LIQUID FECES MIXTURE AT STAFF THROUGH THE TRAY SLOT. 15 DYS RH, 30 DYS LOGT.",
        severity: "Class I",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2024-10-28",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER ROGERS MADE STATEMENTS TO ASSAULT ANOTHER OFFENDER. 15 DYS RH, 15 DYS LOGT.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2024-10-28",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER ROGERS MADE STATEMENTS TO HARM STAFF. 15 DYS RH, 15 DYS LOGT.",
        severity: "Class IIa",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2024-10-28",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER ROGERS MADE STATEMENTS TO ASSAULT ANOTHER OFFENDER. 15 DYS RH, 15 DYS LOGT.",
        severity: "Class IIa",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2024-10-28",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER ROGERS MADE STATEMENTS TO HARM STAFF. 15 DYS RH, 15 DYS LOGT.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2024-09-07",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER MADE STATEMENTS THAT PLACED A PERSON IN FEAR OF INJURY. WAIVED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
      {
        date: "2024-04-20",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "POSSESSION SYRINGE/DRUG PARAPHERNALIA",
        description:
          "OFFENDER ROGERS. IT WAS FOUND THE ITEM WAS NOT A HYPODERMIC NEEDLE AND NOT CAPABLE OF ADMINISTERING DANGEROUS DRUGS. NOTHING IMPOSED.",
        severity: "Class IIa",
        disposition: "NOT GUILTY",
        isPending: false,
      },
      {
        date: "2024-01-14",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "POSSESION OR USE OF DANGEROUS DRUGS",
        description:
          "OFFENDER ROGERS IN POSSESSION OF ITEMS COMMONLY USED IN THE CORRECTION SETTTING TO MAKE HOMEMADE ALCOHOL. 20 DYS LOP PROBATED. OFFENDER WAIVED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
      {
        date: "2023-09-28",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "OFFENDER MADE THREATENING STATEMENTS TO A STAFF MEMBER. OFFENDER WAIVED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2023-09-25",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "VERBAL ABUSE",
        description:
          "OFFENDER ROGERS INTERFERRED WITH A DOC STAFF SEARCH, MAKING OFFENSIVE STATEMENTS AND POSSESSING UNAUTHORIZED ITEMS. 8 DYS RH. OFFENDER WAVIED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2023-09-25",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "INTERFERENCE WITH SEARCH",
        description:
          "OFFENDER ROGERS INTERFERRED WITH A DOC STAFF SEARCH, MAKING OFFENSIVE STATEMENTS AND POSSESSING UNAUTHORIZED ITEMS. 8 DYS RH. OFFENDER WAVIED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2023-09-25",
        facility: "STERLING CORRECTIONAL FACILITY",
        violation: "UNAUTHORIZED POSSESSION",
        description:
          "OFFENDER ROGERS INTERFERRED WITH A DOC STAFF SEARCH, MAKING OFFENSIVE STATEMENTS AND POSSESSING UNAUTHORIZED ITEMS. 8 DYS RH. OFFENDER WAVIED RIGHT TO A FORMAL HEARING.",
        severity: "Class IIa",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2023-06-07",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "TAMPERING WITH LOCKS/SECURITY",
        description:
          "HO KLINGON DO KYPROT: OFFENDER ROGERS HAD THE WINDOW OF THEIR CELL AND REFUSED DIRECTIVES TO UNCOVER THE WINDOW. THEY THEN THREATENED TO THROW WATER ON STAFF.",
        severity: "Class I",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-06",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "DISOBEYING A LAWFUL ORDER - CHARGE A",
        description:
          "HO KLINGON DO KYPROT: OFFENDER COVERED THEIR WINDOW DURING COUNT AND REFUSED DIRECTIVES TO SUBMIT TO RESTRAINTS. OFFENDER MADE MULTIPLE THREATS TO STAFF.",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-06",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "THREATS",
        description:
          "HO KLINGON DO KYPROT: OFFENDER ROGERS MADE THREATS TO KILL STAFF UPON THEIR RELEASE FROM CUSTODY OF THE DEPARTMENT OF CORRECTIONS",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-06",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "COUNT INTERFERENCE",
        description:
          "HO KLINGON DO KYPROT: OFFENDER COVERED THEIR WINDOW DURING COUNT AND REFUSED DIRECTIVES TO SUBMIT TO RESTRAINTS. OFFENDER MADE MULTIPLE THREATS TO STAFF.",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-06",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "TAMPERING WITH LOCKS/SECURITY",
        description:
          "HO KLINGON DO KYPROT: OFFENDER ROGERS MADE THREATS TO KILL STAFF UPON THEIR RELEASE FROM CUSTODY OF THE DEPARTMENT OF CORRECTIONS",
        severity: "Class I",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-06",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "TAMPERING WITH LOCKS/SECURITY",
        description:
          "HO KLINGON DO KYPROT: OFFENDER COVERED THEIR WINDOW DURING COUNT AND REFUSED DIRECTIVES TO SUBMIT TO RESTRAINTS. OFFENDER MADE MULTIPLE THREATS TO STAFF.",
        severity: "Class I",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2023-06-05",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "BODY MODIFICATION",
        description:
          "HO KLINGON DO KYPROT: OFFENDER ROGERS WAS FOUND IN POSSESSION OF TATTOO PARAPHERNALIA",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2023-06-05",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "UNAUTHORIZED POSSESSION",
        description:
          "HO KLINGON DO KYPROT: OFFENDER ROGERS WAS FOUND IN POSSESSION OF TATTOO PARAPHERNALIA",
        severity: "Class IIa",
        disposition: "NO SANCTION - CMNTS",
        isPending: false,
      },
      {
        date: "2022-12-27",
        facility: "CENTENNIAL CORRECTIONAL FACILITY",
        violation: "ASSAULT ON OFFENDER",
        description:
          "HO KLINGON, DO KYPROT: OFFENDER WAS IN A FIGHT WITH ANOTHER OFFENDER",
        severity: "Class I",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2020-01-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "POSSESSION OF DANGEROUS CONTRABAND",
        description:
          "D.O. LT ASTRUD.  H.O. TIP ZAYOOS.  OFFENDER FOUND GUILTY OF COMPLICIT IN ATTEMPTING TO AID TWO OTHERS DURING A MURDER ATTEMPT.  WEAPON FOUND IN THIS OFFENDER'S POSSESSION.",
        severity: "Class I",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2020-01-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "MURDER",
        description:
          "D.O. LT ASTRUD.  H.O. TIP ZAYOOS.  OFFENDER FOUND GUILTY OF COMPLICIT IN ATTEMPTING TO AID TWO OTHERS DURING A MURDER ATTEMPT.  WEAPON FOUND IN THIS OFFENDER'S POSSESSION.",
        severity: "Class I",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2020-01-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "ASSAULT ON OFFENDER",
        description:
          "D.O. LT ASTRUD- OFFENDER AIDED OR ASSISTED WITH OTHER OFFENDERS IN A PHYSICAL ALTERCATION, ASSAULTING ANOTHER OFFENDER WITH WEAPONS.  OFFENDER WAS FOUND IN POSSESSION OF A HOMEMADE WEAPON.",
        severity: "Class I",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2020-01-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "POSSESSION OF DANGEROUS CONTRABAND",
        description:
          "D.O. LT ASTRUD.  H.O. TIP ZAYOOS.  OFFENDER FOUND GUILTY OF COMPLICIT IN ATTEMPTING TO AID TWO OTHERS DURING A MURDER ATTEMPT.  WEAPON FOUND IN THIS OFFENDER'S POSSESSION.",
        severity: "Class I",
        disposition: "SEGREGATION",
        isPending: false,
      },
      {
        date: "2020-01-25",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "POSSESSION OF DANGEROUS CONTRABAND",
        description:
          "D.O. LT ASTRUD- OFFENDER AIDED OR ASSISTED WITH OTHER OFFENDERS IN A PHYSICAL ALTERCATION, ASSAULTING ANOTHER OFFENDER WITH WEAPONS.  OFFENDER WAS FOUND IN POSSESSION OF A HOMEMADE WEAPON.",
        severity: "Class I",
        disposition: "LOST TIME",
        isPending: false,
      },
      {
        date: "2019-06-11",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "THREATS",
        description:
          'D.O. BILL LION. OFFENDER VERBALLY COMMUNICATED AN INTENT TO JEOPARDIZE SAFETY /SECURITY OF FACILITY WHEN HE TOLD OFFICER HE WAS NOT GOING TO FOLLOW THE RULES "SEE WHAT HAPPENS". OFFICER TOOK THIS AS A THREAT.',
        severity: "Class IIa",
        disposition: "HOUSING RESTRICTION",
        isPending: false,
      },
      {
        date: "2018-02-07",
        facility: "COLORADO STATE PENITENTIARY",
        violation: "THREATS",
        description:
          "D.O. SAREN.  OFFENDER VERBALLY AND PHYSICALLY COMMUNICATED A DETERMINATION TO HARM ANOTHER PERSON AND WAS OBSERVED TAMPERING WITH A FIRE ALARM.",
        severity: "Class IIa",
        disposition: "LOST PRIVILEGES",
        isPending: false,
      },
    ],
    docPrograms: [
      {
        name: "Why Try 7.1",
        completionDate: "2021-07-08",
        type: "Mental Health",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
    ],
    edovoPrograms: [],
    offenseHistory: {
      offenses: [
        {
          county: "El Paso County",
          docket: "77CR9088",
          conviction: "Assault",
          classFelony: "Felony Class 4",
          sentence: "24 years",
          dateOfOffense: "2017-07-26",
          convictionDate: "2017-12-06",
          offenseNarrative:
            "A PSI was not available for programming, according to a Probable Cause Affidavit, on 07/26/2017, while several El Paso County Deputies were attempting to stop offender Rogers from harming himself, offender Rogers spit saliva at the deputies, hitting one in the face, right eye and shoulder.",
        },
      ],
      priorConvictions: [
        {
          charge: "Assault",
          date: "2012-02-14",
        },
        {
          charge: "Menacing",
          date: "2008-02-21",
        },
        {
          charge: "Menacing",
          date: "2017-09-12",
        },
      ],
      victimInvolved: true,
      victimAttendingHearing: false,
    },
    riskAndNeedsFactors: buildRiskAndNeedsFactors(2),
    riskAssessments: [
      {
        tool: "CARAS",
        score: 0.61,
        maxScore: 1,
        date: "2026-07-14",
      },
      {
        tool: "SRT",
        score: 17,
        maxScore: 44,
        date: "2026-04-07",
      },
      {
        tool: "CARAS",
        score: 0.63,
        maxScore: 1,
        date: "2025-10-29",
      },
      {
        tool: "CARAS",
        score: 0.71,
        maxScore: 1,
        date: "2025-09-13",
      },
      {
        tool: "RT",
        score: 14,
        maxScore: 27,
        date: "2025-03-31",
      },
      {
        tool: "RT",
        score: 13,
        maxScore: 27,
        date: "2024-03-19",
      },
      {
        tool: "SRT",
        score: 17,
        maxScore: 44,
        date: "2023-03-20",
      },
      {
        tool: "SRT",
        score: 24,
        maxScore: 44,
        date: "2021-04-26",
      },
      {
        tool: "SRT",
        score: 23,
        maxScore: 44,
        date: "2020-04-22",
      },
      {
        tool: "SRT",
        score: 25,
        maxScore: 44,
        date: "2019-04-24",
      },
      {
        tool: "SRT",
        score: 19,
        maxScore: 44,
        date: "2018-04-23",
      },
      {
        tool: "PIT",
        score: 28,
        maxScore: 39,
        date: "2018-01-24",
      },
      {
        tool: "SRT",
        score: 19,
        maxScore: 44,
        date: "2015-12-06",
      },
      {
        tool: "SRT",
        score: 18,
        maxScore: 44,
        date: "2015-04-09",
      },
    ],
  }),
};

// Case-profile detail for each ID_HEARINGS entry, keyed by pseudonymizedId.
// Analogous to CO_ANONYMIZED_CASE_PROFILES above, but hand-authored as
// synthetic data from the start rather than anonymized from a sample.
const ID_SYNTHETIC_CASE_PROFILES: Record<string, ParoleCase> = {
  "350463": paroleCaseSchema.parse({
    pseudonymizedId: "350463",
    displayId: "350463",
    name: "STARK, TONY",
    dob: "1987-03-22",
    gender: "Male",
    currentFacility: "Central State Correctional Facility",
    custodyLevel: "Close",
    caseManagerName: "Angela Wright",
    hearingDate: iso(addDays(new Date(), 35)),
    hearingType: "Parole Grant Hearing",
    isParoleReturn: true,
    sentenceStartDate: iso(subYears(new Date(), 8)),
    paroleEligibilityDate: iso(addDays(new Date(), 90)),
    mandatoryReleaseDate: iso(addYears(new Date(), 13)),
    parolePlan: {
      onFile: false,
      documents: [],
    },
    attachments: [],
    conductHistory: [
      buildConductRecord(1, {
        facility: "Western State Prison",
        violation: "Assault on Staff",
        description:
          "Threw an unknown liquid substance at a correctional officer during meal distribution.",
        disposition: "45 days disciplinary segregation, loss of good time",
        isPending: false,
        severity: "Class A",
      }),
      buildConductRecord(3, {
        facility: "Western State Prison",
        violation: "Threatening Behavior",
        description: "Made verbal threats of violence toward a staff member.",
        disposition: "30 days disciplinary segregation",
        isPending: false,
        severity: "Class A",
      }),
      buildConductRecord(5, {
        facility: "Central State Correctional Facility",
        violation: "Possession of Contraband",
        description:
          "Found in possession of an unauthorized weapon during a routine cell search.",
        disposition: "60 days disciplinary segregation, loss of good time",
        isPending: false,
        severity: "Class A",
      }),
      buildConductRecord(9, {
        facility: "Central State Correctional Facility",
        violation: "Property Damage",
        description: "Damaged state-issued property during a cell search.",
        disposition: "Restitution ordered",
        isPending: false,
        severity: "Class C",
      }),
      buildConductRecord(14, {
        facility: "North River Correctional Center",
        violation: "Fighting",
        description:
          "Physical altercation with another incarcerated individual in the yard.",
        disposition: "30 days disciplinary segregation",
        isPending: false,
        severity: "Class A",
      }),
      buildConductRecord(20, {
        facility: "North River Correctional Center",
        violation: "Unauthorized Possession",
        description:
          "Found with unauthorized tattoo paraphernalia during a cell inspection.",
        disposition: "Loss of privileges - 14 days",
        isPending: false,
        severity: "Class C",
      }),
      buildConductRecord(27, {
        facility: "South Bay Detention Center",
        violation: "Assault on Staff",
        description:
          "Struck a correctional officer during an escort to segregation.",
        disposition: "Disciplinary segregation, referred for prosecution",
        isPending: false,
        severity: "Class A",
      }),
      buildConductRecord(33, {
        facility: "South Bay Detention Center",
        violation: "Threatening Behavior",
        description:
          "Made threats of harm toward staff following a disciplinary hearing.",
        disposition: "30 days disciplinary segregation",
        isPending: false,
        severity: "Class A",
      }),
    ],
    docPrograms: [
      {
        name: "Why Try 7.1",
        completionDate: "2021-07-08",
        type: "Mental Health",
        criminogenicNeed: "",
        status: "DISCHARGED_SUCCESSFUL",
      },
    ],
    edovoPrograms: [],
    offenseHistory: {
      offenses: [
        {
          county: "Sample County",
          docket: "2017-CR-9088",
          conviction: "Assault",
          classFelony: "Felony Class 4",
          sentence: "24 years",
          dateOfOffense: "2017-07-26",
          convictionDate: "2017-12-06",
          offenseNarrative:
            "Defendant assaulted a law enforcement officer while being taken into custody.",
          statute: "18-901",
          sentencingDate: "2017-12-06",
          sentenceStartDate: "2017-12-06",
          paroleEligibilityDate: iso(addDays(new Date(), 90)),
          fullTermDate: iso(addYears(new Date(), 13)),
          indeterminateStartDate: iso(addDays(new Date(), 90)),
          indeterminateEndDateInclusive: iso(addYears(new Date(), 13)),
        },
      ],
      priorConvictions: [
        { charge: "Assault", date: "2012-02-14" },
        { charge: "Menacing", date: "2008-02-21" },
        { charge: "Menacing", date: "2017-09-12" },
      ],
      victimInvolved: true,
      victimAttendingHearing: true,
    },
    riskAssessments: [
      {
        tool: "LSIR",
        score: 30,
        maxScore: 54,
        date: "2026-08-19",
      },
      {
        tool: "VRAG",
        score: 29,
        maxScore: 38,
        date: "2026-08-19",
      },
      {
        tool: "STATIC_99",
        score: 4,
        maxScore: 12,
        date: "2026-08-19",
      },
      {
        tool: "STABLE",
        score: 4,
        maxScore: 26,
        date: "2026-08-19",
      },
    ],
    communitySupervisionPlan: [],
    riskAndNeedsFactors: buildRiskAndNeedsFactors(0),
  }),
};

/**
 * Fills in each assessment's level, so the literals above do not all have to
 * spell one out. A real level is recorded by the assessing tool rather than
 * derived from the score; this stands in for that, and mimics how the two
 * states spell it -- US_ID upper-case, US_CO title-case.
 */
function withAssessmentLevels(
  caseDetail: ParoleCase,
  stateCode: ParoleFixtureStateCode,
): ParoleCase {
  return {
    ...caseDetail,
    riskAssessments: caseDetail.riskAssessments.map((assessment) => {
      if (assessment.level || !assessment.maxScore) return assessment;
      const pct = (assessment.score / assessment.maxScore) * 100;
      let level = "Low";
      if (pct >= 60) level = "High";
      else if (pct >= 30) level = "Medium";
      return {
        ...assessment,
        level: stateCode === "US_ID" ? level.toUpperCase() : level,
      };
    }),
  };
}

/**
 * Idaho's real note types, from Atlas ref_NoteType. Listed by how often each
 * one occurs, so the fixture's mix resembles a real record: the first two
 * carry the bulk of the volume.
 */
const ID_CASE_NOTE_TYPES = [
  "Supervision Notes",
  "Facility Notes",
  "Case Update",
  "Parole Board Note",
  "PSI",
] as const;

// Sentences the fixture strings together into note bodies. Idaho's real
// notes average around 340 characters and the longest run into the
// thousands, so a note here is built long enough to truncate in the list.
const ID_CASE_NOTE_SENTENCES = [
  "Met with resident in the unit to review progress toward the current case plan goals.",
  "Resident reports steady attendance in programming and no conflicts with unit staff.",
  "Discussed release planning, including residence verification and transportation.",
  "Resident asked about the timeline for the next board review and was given the schedule.",
  "No new concerns raised by unit staff since the previous contact.",
  "Employment coordinator was contacted regarding placement options after release.",
  "Resident completed the assigned workbook section and returned it for review.",
  "Family contact remains consistent; resident reports weekly phone calls.",
];

/**
 * The number of notes each Idaho fixture case carries. Chosen to fill more
 * than one page, so the list's pagination is demoable in offline mode.
 */
const ID_CASE_NOTE_COUNT = 32;

/**
 * Builds a case's notes, newest first, roughly one every three weeks going
 * back. Everything varies off the case's index, so a given pseudonymizedId gets the
 * same notes on every load.
 *
 * @param index - The case's position in the fixture list.
 * @param today - The date the fixture is generated relative to.
 */
function buildIdCaseNotes(index: number, today: Date): Array<ParoleCaseNote> {
  return Array.from({ length: ID_CASE_NOTE_COUNT }, (_, noteIndex) => {
    const seed = index * 7 + noteIndex;
    const sentenceCount = 2 + (seed % 7);
    const body = Array.from(
      { length: sentenceCount },
      (__, sentenceIndex) =>
        ID_CASE_NOTE_SENTENCES[
          (seed + sentenceIndex * 3) % ID_CASE_NOTE_SENTENCES.length
        ],
    ).join(" ");

    return {
      id: `note-${index}-${noteIndex}`,
      type: ID_CASE_NOTE_TYPES[seed % ID_CASE_NOTE_TYPES.length],
      date: iso(subDays(today, noteIndex * 21 + (seed % 5))),
      body,
    };
  });
}

/**
 * Attaches case notes to Idaho's cases. No state's backend sends them yet,
 * so Colorado's cases keep none and its section stays hidden.
 *
 * @param caseDetail - The case to attach notes to.
 * @param index - The case's position in the fixture list.
 * @param stateCode - The tenant the case is built for.
 */
function withCaseNotes(
  caseDetail: ParoleCase,
  index: number,
  stateCode: ParoleFixtureStateCode,
): ParoleCase {
  if (stateCode !== "US_ID" || caseDetail.caseNotes) return caseDetail;
  return { ...caseDetail, caseNotes: buildIdCaseNotes(index, new Date()) };
}

function buildParoleCasesFixture(
  stateCode: ParoleFixtureStateCode,
): Record<string, ParoleCase> {
  return Object.fromEntries(
    paroleHearingsFixtureByState[stateCode].map((hearing, index) => {
      const caseDetail = (() => {
        if (hearing.pseudonymizedId === "45821") {
          return buildAndersonCaseProfile(hearing.hearingDate, stateCode);
        }
        if (hearing.pseudonymizedId in CO_ANONYMIZED_CASE_PROFILES) {
          return CO_ANONYMIZED_CASE_PROFILES[hearing.pseudonymizedId];
        }
        if (hearing.pseudonymizedId in ID_SYNTHETIC_CASE_PROFILES) {
          return ID_SYNTHETIC_CASE_PROFILES[hearing.pseudonymizedId];
        }
        return buildGenericCaseProfile(hearing, index, stateCode);
      })();

      return [
        hearing.pseudonymizedId,
        withCaseNotes(
          withAssessmentLevels(caseDetail, stateCode),
          index,
          stateCode,
        ),
      ];
    }),
  );
}

// Keyed by state so `nx offline staff` can serve each Parole-enabled
// tenant its own conduct classification scheme (see ParoleOfflineAPIClient)
// -- the rest of the case data (offense history, risk assessments, etc.) is
// identical across states, only conductHistory severities differ.
export const paroleCasesFixtureByState: Record<
  ParoleFixtureStateCode,
  Record<string, ParoleCase>
> = {
  US_CO: buildParoleCasesFixture("US_CO"),
  US_ID: buildParoleCasesFixture("US_ID"),
};
